"use client";

import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import type { Locale } from "@/lib/i18n/locales";
import type { QuizId } from "./registry";
import {
  BUS_QUIZ_CLEAR_EVENT,
  quizStorageKeys,
  BUS_QUIZ_COMPLETION_PREFIX,
} from "./bus-quiz-storage";

type QuizCompletionTickProps = {
  quizId: QuizId;
  locale?: Locale;
  persistenceRevision?: string;
  className?: string;
};

export default function QuizCompletionTick({
  quizId,
  locale = "en",
  persistenceRevision,
  className,
}: QuizCompletionTickProps) {
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const key = quizStorageKeys(quizId, locale, persistenceRevision).completion;
    const updateCompletion = () => {
      try {
        setIsComplete(Boolean(window.localStorage.getItem(key)));
      } catch (error) {
        console.warn("Failed to read completion state", error);
        setIsComplete(false);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || !event.key.startsWith(BUS_QUIZ_COMPLETION_PREFIX)) {
        return;
      }
      updateCompletion();
    };

    updateCompletion();
    window.addEventListener("storage", handleStorage);
    window.addEventListener(BUS_QUIZ_CLEAR_EVENT, updateCompletion);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(BUS_QUIZ_CLEAR_EVENT, updateCompletion);
    };
  }, [quizId, locale, persistenceRevision]);

  if (!isComplete) return null;

  const classes = className
    ? `absolute pointer-events-none ${className}`
    : "absolute -top-1 -right-1 pointer-events-none";

  return (
    <span className={classes} aria-hidden="true">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 shadow ring-2 ring-white">
        <CheckCircle2 className="h-3 w-3 text-white" />
      </span>
    </span>
  );
}
